import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Checkbox,
  InputAdornment,
  TableSortLabel
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, RefreshCw, Printer as PrintIcon, Search, ListChecks as StatusLogoIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';

export default function StatiPratica() {
  const [stati, setStati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentStato, setCurrentStato] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    label: '',
    is_closed: false,
    sort_order: 0
  });
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const { addJob } = useJob();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('sort_order');
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    fetchStati();
  }, []);

  const fetchStati = async () => {
    setLoading(true);
    try {
      const response = await api.get('/stati-pratica');
      setStati(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching stati pratica:', err);
      setError('Failed to load data');
      toast.error('Errore nel caricamento degli stati pratica');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (stato = null) => {
    if (stato) {
      setCurrentStato(stato);
      setFormData({
        code: stato.code,
        label: stato.label,
        is_closed: !!stato.is_closed,
        sort_order: stato.sort_order
      });
    } else {
      setCurrentStato(null);
      setFormData({
        code: '',
        label: '',
        is_closed: false,
        sort_order: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentStato(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (currentStato) {
        await api.put(`/stati-pratica/${currentStato.id}`, formData);
        toast.success('Stato aggiornato con successo');
      } else {
        await api.post('/stati-pratica', formData);
        toast.success('Stato creato con successo');
      }
      fetchStati();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving stato:', err);
      toast.error(err.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/stati-pratica/${deleteTarget.id}`);
      toast.success('Stato eliminato con successo');
      closeDeleteDialog();
      fetchStati();
    } catch (err) {
      console.error('Error deleting stato:', err);
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = stati.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePrintPdf = async () => {
    try {
      const startRes = await api.post('/stati-pratica/report', { ids: selected });
      const jobId = startRes.data.jobId;
      addJob(jobId, 'stato_pratica');
      setSelected([]);
    } catch (error) {
      console.error('Error starting report job:', error);
      toast.error('Errore nell\'avvio della generazione del PDF');
    }
  };

  const filteredStati = stati
    .filter((row) => {
      if (!filterText) return true;
      const search = filterText.toLowerCase();
      const code = row.code ? row.code.toString().toLowerCase() : '';
      const label = row.label ? row.label.toString().toLowerCase() : '';
      return code.includes(search) || label.includes(search);
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      const key = orderBy || 'sort_order';
      const valA = a[key];
      const valB = b[key];
      if (key === 'sort_order' || key === 'id') {
        const numA = valA == null ? 0 : Number(valA);
        const numB = valB == null ? 0 : Number(valB);
        return isAsc ? numA - numB : numB - numA;
      }
      const strA = valA ? valA.toString().toLowerCase() : '';
      const strB = valB ? valB.toString().toLowerCase() : '';
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return 0;
    });

  return (
    <Box className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <StatusLogoIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Stati Pratica
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Elenco e configurazione degli stati per il workflow delle pratiche
            </Typography>
          </div>
        </div>
        <Box>
          <Button 
            onClick={handlePrintPdf} 
            disabled={selected.length === 0}
            variant="secondary"
            className="mr-2"
          >
            <PrintIcon className="mr-2 h-4 w-4" />
            Stampa PDF
          </Button>
          <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
            <PlusIcon size={16} />
            Nuovo Stato
          </Button>
        </Box>
      </div>

      {error && <Alert severity="error" className="mb-4">{error}</Alert>}

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterText}
            onChange={handleFilterChange}
            placeholder="Cerca per codice o etichetta..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < stati.length}
                    checked={stati.length > 0 && selected.length === stati.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'code'}
                    direction={orderBy === 'code' ? order : 'asc'}
                    onClick={() => handleRequestSort('code')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'label'}
                    direction={orderBy === 'label' ? order : 'asc'}
                    onClick={() => handleRequestSort('label')}
                  >
                    Etichetta
                  </TableSortLabel>
                </TableCell>
                <TableCell>Chiuso</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'sort_order'}
                    direction={orderBy === 'sort_order' ? order : 'asc'}
                    onClick={() => handleRequestSort('sort_order')}
                  >
                    Ordine
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8">
                    <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : stati.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8 text-gray-500">
                    Nessuno stato trovato
                  </TableCell>
                </TableRow>
              ) : (
                filteredStati
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const isSelected = selected.indexOf(row.id) !== -1;
                    return (
                    <TableRow 
                      key={row.id} 
                      hover
                      role="checkbox"
                      aria-checked={isSelected}
                      selected={isSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onChange={(event) => handleClick(event, row.id)}
                        />
                      </TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      <TableCell>
                        <Chip 
                          label={row.is_closed ? "Sì" : "No"} 
                          color={row.is_closed ? "default" : "success"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.sort_order}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary">
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton size="small" onClick={() => openDeleteDialog(row)} color="error">
                          <DeleteIcon size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredStati.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentStato ? 'Modifica Stato Pratica' : 'Nuovo Stato Pratica'}
        </DialogTitle>
        <DialogContent dividers className="space-y-4">
          <TextField
            autoFocus
            margin="dense"
            name="code"
            label="Codice (univoco)"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.code}
            onChange={handleChange}
            helperText="Es: aperta, in_lavorazione, chiusa"
          />
          <TextField
            margin="dense"
            name="label"
            label="Etichetta (visibile)"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.label}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="sort_order"
            label="Ordine di visualizzazione"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.sort_order}
            onChange={handleChange}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_closed}
                onChange={handleChange}
                name="is_closed"
                color="primary"
              />
            }
            label="Indica pratica chiusa"
            className="mt-2"
          />
        </DialogContent>
        <DialogActions className="p-4">
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.code || !formData.label}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Questa azione è irreversibile.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Stai per eliminare lo stato pratica:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
            {deleteTarget?.label || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Codice: <strong>{deleteTarget?.code || '-'}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
